import { Space } from '@tribeplatform/gql-client/types'
import { I18nShape } from '@tribeplatform/react-components/i18n'

import {
  CollectionMenuDetailedItem,
  CollectionMenuDetailedSpaceItem,
  CollectionMenuEntry,
  CollectionMenuItem,
  CollectionMenuSpaceFilter,
  CollectionMenuSpaceItem,
  CollectionMenuViewStyle,
  NO_COLLECTION_ID,
  NoCollectionEntry,
  collapseLocaleGroupSpaces,
  isNoCollectionEntry,
} from './types.js'

export const getItemsByIds = (
  items: CollectionMenuItem[],
): Record<string, CollectionMenuItem & { index: number }> => {
  const itemsById: Record<string, CollectionMenuItem & { index: number }> = {}
  items.forEach((item, index) => {
    itemsById[item.id] = { ...item, index }
  })
  return itemsById
}

export const getOrderedCollections = (
  items: CollectionMenuItem[],
  collections: CollectionMenuEntry[],
  filterEnabled = true,
): CollectionMenuEntry[] => {
  const itemsById = getItemsByIds(items)
  const orderedCollections = collections
    .filter(
      c => itemsById[c.id] && (!filterEnabled || itemsById[c.id]?.enabled),
    )
    .sort((c1, c2) => itemsById[c1.id].index - itemsById[c2.id].index)
  const newCollections = collections
    .filter(c => !itemsById[c.id])
    .sort((c1, c2) => c1.customOrderingIndex - c2.customOrderingIndex)
  return [...orderedCollections, ...newCollections]
}

export const getDetailedCollectionItems = (
  items: CollectionMenuItem[],
  collections: CollectionMenuEntry[],
): CollectionMenuDetailedItem[] => {
  const itemsById = getItemsByIds(items)
  const orderedCollections = getOrderedCollections(items, collections, false)
  return orderedCollections.map(c => {
    const stored = itemsById[c.id]
    const isSynthetic = c.id === NO_COLLECTION_ID
    return {
      id: c.id,
      name: c.name,
      enabled:
        typeof stored?.enabled === 'undefined' ? !isSynthetic : stored.enabled,
      ...(stored?.spaces ? { spaces: stored.spaces } : {}),
    }
  })
}

/**
 * A locale group renders as a single nav entry, so its members are organized
 * as one unit: the stored item id for any group member is the group id, and
 * enabling/ordering applies to every variant no matter which one the nav
 * displays for the viewer's locale.
 *
 * The identity this derives is therefore tied to *current* group membership,
 * and a saved config has no migration path across a membership change: a
 * space that later joins a group is looked up under the group id, a variant
 * that later leaves one is looked up under its own id, and either way the
 * lookup misses and the space is treated as new — appended in server order
 * and enabled. It fails open, so a deliberately hidden space silently comes
 * back rather than a visible one disappearing. Reconciling both identities
 * would mean persisting the member space ids alongside the group id; not
 * worth it while grouping is effectively a one-time setup step.
 */
export const getSpaceItemId = (space: Space): string =>
  space.localeGroupId || space.id

/**
 * Orders an entry's spaces by their stored index, appending spaces without a
 * stored item (e.g. newly created ones) in server order — the same semantics
 * as `getOrderedCollections`. With `filterEnabled`, spaces toggled off in the
 * organize modal are dropped.
 */
export const getOrderedEntrySpaces = (
  spaceItems: CollectionMenuSpaceItem[],
  spaces: Space[],
  filterEnabled = true,
): Space[] => {
  const itemsById = getItemsByIds(spaceItems)
  const orderedSpaces = spaces
    .filter(s => {
      const item = itemsById[getSpaceItemId(s)]
      return item && (!filterEnabled || item.enabled)
    })
    .sort(
      (s1, s2) =>
        itemsById[getSpaceItemId(s1)].index -
        itemsById[getSpaceItemId(s2)].index,
    )
  const newSpaces = spaces.filter(s => !itemsById[getSpaceItemId(s)])
  return [...orderedSpaces, ...newSpaces]
}

export const getDetailedSpaceItems = (
  spaceItems: CollectionMenuSpaceItem[],
  spaces: Space[],
): CollectionMenuDetailedSpaceItem[] => {
  const itemsById = getItemsByIds(spaceItems)
  return getOrderedEntrySpaces(spaceItems, spaces, false).map(space => ({
    id: getSpaceItemId(space),
    enabled: itemsById[getSpaceItemId(space)]?.enabled ?? true,
    space,
  }))
}

/**
 * How many rows the views end up rendering for a space list: the render path
 * collapses each locale group to a single entry, so the raw list length would
 * over-count a group with several variants. The locale only decides *which*
 * variant is kept, never how many, so the count needs no locale.
 */
const getRenderedCount = (spaces: Space[]): number =>
  collapseLocaleGroupSpaces(spaces).length

/**
 * Applies each entry's stored space order/visibility to the entry's fetched
 * spaces, so every view style renders them the way the organize modal saved
 * them. Entries without a stored space config pass through unchanged.
 */
export const applyStoredSpaceItems = (
  items: CollectionMenuItem[],
  entries: CollectionMenuEntry[],
): CollectionMenuEntry[] => {
  const itemsById = getItemsByIds(items)
  return entries.map(entry => {
    const spaceItems = itemsById[entry.id]?.spaces
    if (!spaceItems?.length || !entry.spaces) {
      return entry
    }
    if (isNoCollectionEntry(entry)) {
      const nodes = getOrderedEntrySpaces(spaceItems, entry.spaces.nodes ?? [])
      return {
        ...entry,
        spaces: { ...entry.spaces, nodes, totalCount: getRenderedCount(nodes) },
      }
    }
    const edges = entry.spaces.edges ?? []
    const edgesBySpaceId = new Map(edges.map(edge => [edge.node.id, edge]))
    const orderedSpaces = getOrderedEntrySpaces(
      spaceItems,
      edges.map(({ node }) => node),
    )
    // totalCount mirrors what the views can render, so their "has spaces"
    // gates stay consistent with the filtered list.
    return {
      ...entry,
      spaces: {
        ...entry.spaces,
        edges: orderedSpaces.flatMap(space => {
          const edge = edgesBySpaceId.get(space.id)
          return edge ? [edge] : []
        }),
        nodes: orderedSpaces,
        totalCount: getRenderedCount(orderedSpaces),
      },
    }
  })
}

export const buildNoCollectionEntry = (
  name: string,
  spaces?: NoCollectionEntry['spaces'],
): NoCollectionEntry => ({
  id: NO_COLLECTION_ID,
  name,
  customOrderingIndex: Number.MAX_SAFE_INTEGER,
  spaces,
})

export const viewStylesFactory = ({ $t }: I18nShape) => [
  {
    value: CollectionMenuViewStyle.Simple,
    text: $t({ id: 'Generics.Simple', defaultMessage: 'Simple' }),
  },
  {
    value: CollectionMenuViewStyle.Collapsible,
    text: $t({
      id: 'Generics.Collapsible',
      defaultMessage: 'Collapsible',
    }),
  },
  {
    value: CollectionMenuViewStyle.Heading,
    text: $t({ id: 'Generics.Heading', defaultMessage: 'Heading' }),
  },
]

export const spaceFiltersFactory = ({ $t }: I18nShape) => [
  {
    value: CollectionMenuSpaceFilter.All,
    text: $t({
      id: 'CollectionMenuBlock.Settings.All',
      defaultMessage: 'All spaces',
    }),
  },
  {
    value: CollectionMenuSpaceFilter.Joined,
    text: $t({
      id: 'CollectionMenuBlock.Settings.Joined',
      defaultMessage: 'Only joined spaces',
    }),
  },
  {
    value: CollectionMenuSpaceFilter.NotJoined,
    text: $t({
      id: 'CollectionMenuBlock.Settings.NotJoined',
      defaultMessage: 'Only not joined spaces',
    }),
  },
]
